// ===============================================================
//  Collectors Component - Reworld Facilities Manager
//  --------------------------------------------------------------
//  • Displays Reworld facility data from JSON
//  • Google Maps integration for viewing facilities
//  • Search by location (within radius) and keywords
//  • Filter by facility type and services
//  • Team-based access control managed by TeamAccessWrapper
// ===============================================================

import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  FaSearch,
  FaSpinner,
  FaMapMarkerAlt,
  FaBuilding,
  FaFilter,
  FaEye,
  FaPhone,
  FaGlobe,
  FaExternalLinkAlt,
  FaMap,
  FaTimes,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { AuthContext } from "../../AuthProvider";
import { getAuth } from "firebase/auth";

// ---------------------------------------------------------------------------
// CONSTANTS & CONFIG
// ---------------------------------------------------------------------------
const ADMIN_UIDS = [
  "wVdZ4zMt1PYD6mmNflk6ZApiZEe2",
  "LRPoMCWtpFRT0tcK2bTi90HbVSZ2",
];
const ENABLE_LOGGING = true;
const DEFAULT_RADIUS = 100; // miles
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const log = (...args) => ENABLE_LOGGING && console.log("[Collectors]", ...args);

// Component will fetch facilities data from /collectors.json

// Helper function to calculate distance between two coordinates
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Radius of Earth in miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// =============================================================
//                     COLLECTORS COMPONENT
// =============================================================
const Collectors = ({ teamId }) => {
  const { user } = useContext(AuthContext);
  const isAdmin = user && ADMIN_UIDS.includes(user.uid);

  // State management - ALL hooks must be called before any conditional returns
  const [facilities, setFacilities] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterService, setFilterService] = useState("");
  const [filterState, setFilterState] = useState("");
  const [filterCity, setFilterCity] = useState("");

  // Map and location search states
  const [showMap, setShowMap] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [searchLocation, setSearchLocation] = useState("");
  const [searchRadius, setSearchRadius] = useState(DEFAULT_RADIUS);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [nearbyFacilities, setNearbyFacilities] = useState([]);
  const [mapCenter, setMapCenter] = useState(null);

  // Modal states
  const [showDetail, setShowDetail] = useState(false);
  const [showLocationSearch, setShowLocationSearch] = useState(false);

  // Renewable businesses states
  const [renewableBusinesses, setRenewableBusinesses] = useState([]);
  const [showRenewableModal, setShowRenewableModal] = useState(false);
  const [renewableLoading, setRenewableLoading] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);

  // Renewable businesses pagination
  const [renewablePage, setRenewablePage] = useState(1);
  const [renewableItemsPerPage] = useState(10);

  // ------------------------------------------------------------------
  // Load facilities data from /collectors.json on mount
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!user || !teamId) return;

    const loadFacilities = async () => {
      try {
        setLoading(true);
        log("Fetching facilities from /collectors.json for team:", teamId);

        // Fetch from the public directory at root
        const response = await fetch("/collectors.json");
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        log("Fetched data:", data);

        // Extract facilities array from the JSON structure
        const facilitiesData = data.facilities || [];

        // Set the facilities and filtered data
        setFacilities(facilitiesData);
        setFiltered(facilitiesData);

        // Show success message
        toast.success(
          `Loaded ${facilitiesData.length} facilities from collectors.json`
        );
        log(`Successfully loaded ${facilitiesData.length} facilities`);
      } catch (error) {
        console.error("Error loading facilities:", error);
        toast.error(`Failed to load facilities: ${error.message}`);

        // Set empty arrays on error
        setFacilities([]);
        setFiltered([]);
      } finally {
        setLoading(false);
      }
    };

    loadFacilities();
  }, [user, teamId]);

  // ------------------------------------------------------------------
  // Filter facilities based on search criteria
  // ------------------------------------------------------------------
  useEffect(() => {
    let results = [...facilities];

    // Text search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      results = results.filter(
        (f) =>
          f.name.toLowerCase().includes(term) ||
          f.city.toLowerCase().includes(term) ||
          f.state.toLowerCase().includes(term) ||
          f.address.toLowerCase().includes(term) ||
          f.services.some((s) => s.toLowerCase().includes(term))
      );
    }

    // Filter by type
    if (filterType) {
      results = results.filter((f) => f.facility_type === filterType);
    }

    // Filter by service
    if (filterService) {
      results = results.filter((f) => f.services.includes(filterService));
    }

    // Filter by state
    if (filterState) {
      results = results.filter((f) => f.state === filterState);
    }

    // Filter by city
    if (filterCity) {
      results = results.filter((f) => f.city === filterCity);
    }

    setFiltered(results);
    setCurrentPage(1);
  }, [
    searchTerm,
    filterType,
    filterService,
    filterState,
    filterCity,
    facilities,
  ]);

  // ------------------------------------------------------------------
  // Search facilities by location and radius
  // ------------------------------------------------------------------
  const searchByLocation = async () => {
    if (!searchLocation) {
      toast.error("Please enter a location");
      return;
    }

    setSearching(true);
    try {
      // Geocode the search location
      const geocodeUrl = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(
        searchLocation
      )}&key=${GOOGLE_MAPS_API_KEY}`;
      const geocodeResponse = await fetch(geocodeUrl);
      const geocodeData = await geocodeResponse.json();

      if (geocodeData.status !== "OK" || !geocodeData.results.length) {
        throw new Error("Location not found");
      }

      const { lat, lng } = geocodeData.results[0].geometry.location;
      setMapCenter({ lat, lng });

      // Filter facilities within radius
      let nearby = facilities.filter((facility) => {
        const distance = calculateDistance(
          lat,
          lng,
          facility.coordinates.latitude,
          facility.coordinates.longitude
        );
        return distance <= searchRadius;
      });

      // Apply keyword filter if provided
      if (searchKeyword) {
        const keyword = searchKeyword.toLowerCase();
        nearby = nearby.filter(
          (f) =>
            f.name.toLowerCase().includes(keyword) ||
            f.services.some((s) => s.toLowerCase().includes(keyword)) ||
            f.facility_type.toLowerCase().includes(keyword)
        );
      }

      // Add distance to results
      nearby = nearby
        .map((f) => ({
          ...f,
          distance: calculateDistance(
            lat,
            lng,
            f.coordinates.latitude,
            f.coordinates.longitude
          ),
        }))
        .sort((a, b) => a.distance - b.distance);

      setNearbyFacilities(nearby);
      setShowLocationSearch(true);
      toast.success(
        `Found ${nearby.length} facilities within ${searchRadius} miles`
      );
    } catch (error) {
      console.error("Location search error:", error);
      toast.error("Failed to search by location");
    } finally {
      setSearching(false);
    }
  };

  // ------------------------------------------------------------------
  // Google Places search for renewable-energy manufacturing companies
  // ------------------------------------------------------------------
  const findSimilarFacilities = async (facility) => {
    try {
      setRenewableLoading(true);
      const token = user ? await getAuth().currentUser.getIdToken(true) : null;

      const apiBase =
        process.env.NODE_ENV === "development"
          ? "http://localhost:5001/json-storage-bed47/us-central1/api"
          : "https://us-central1-json-storage-bed47.cloudfunctions.net/api";

      const params = new URLSearchParams({
        lat: facility.coordinates.latitude,
        lng: facility.coordinates.longitude,
        radiusMiles: searchRadius,
        keywords: searchKeyword || "solar,wind turbine,manufacturing",
        provider: "google",
        facilityId: facility.id, // purely informational (optional)
        pageLimit: 2, // Limit to 2 pages (~25-40 results)
      });

      const response = await fetch(`${apiBase}/renewables?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();

      if (!data.ok) throw new Error(data.error || "Search failed");

      console.log("Renewables result:", data);

      // Store the renewable businesses data
      setRenewableBusinesses(data.businesses || []);
      setRenewablePage(1); // Reset pagination
      setShowRenewableModal(true); // Show the modal

      toast.success(`Found ${data.businesses.length} renewable businesses`);
    } catch (err) {
      console.error("Renewables search error:", err);
      toast.error(`Failed: ${err.message}`);
    } finally {
      setRenewableLoading(false);
    }
  };

  // ------------------------------------------------------------------
  // Helper functions
  // ------------------------------------------------------------------
  const clearFilters = () => {
    setSearchTerm("");
    setFilterType("");
    setFilterService("");
    setFilterState("");
    setFilterCity("");
    setSearchLocation("");
    setSearchKeyword("");
    setSearchRadius(DEFAULT_RADIUS);
    setNearbyFacilities([]);
  };

  const openGoogleMaps = (facility) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      facility.address
    )}`;
    window.open(url, "_blank");
  };

  const viewFacilityDetail = (facility) => {
    setSelectedFacility(facility);
    setShowDetail(true);
  };

  // ------------------------------------------------------------------
  // Computed values
  // ------------------------------------------------------------------
  const facilityTypes = useMemo(
    () => [...new Set(facilities.map((f) => f.facility_type))].sort(),
    [facilities]
  );

  const allServices = useMemo(
    () => [...new Set(facilities.flatMap((f) => f.services))].sort(),
    [facilities]
  );

  const states = useMemo(
    () => [...new Set(facilities.map((f) => f.state))].sort(),
    [facilities]
  );

  const cities = useMemo(() => {
    const stateFacilities = filterState
      ? facilities.filter((f) => f.state === filterState)
      : facilities;
    return [...new Set(stateFacilities.map((f) => f.city))].sort();
  }, [facilities, filterState]);

  // Pagination
  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedData = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Early return if no team ID
  if (!teamId) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="p-4 bg-yellow-50 text-yellow-700 rounded-lg">
          No team selected. Please ensure you have access to a team.
        </div>
      </div>
    );
  }

  // ===========================================================
  //                        RENDER
  // ===========================================================
  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Facility Collectors</h1>
          <p className="text-gray-600">
            Manage and search Reworld waste facilities
          </p>
        </div>
        <div className="flex items-center space-x-4 mt-4 md:mt-0">
          <span className="text-sm text-gray-500">
            {filtered.length} of {facilities.length} facilities
          </span>
          <button
            onClick={() => setShowMap(!showMap)}
            className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            <FaMap />
            <span>{showMap ? "Hide Map" : "Show Map"}</span>
          </button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        {/* Search bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-4">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search by name, city, state, or service..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 pl-10 border rounded-md focus:ring-2 focus:ring-blue-500"
            />
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          </div>

          {/* Location search button */}
          <button
            onClick={() => setShowLocationSearch(!showLocationSearch)}
            className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            <FaMapMarkerAlt />
            <span>Search by Location</span>
          </button>
        </div>

        {/* Location search panel */}
        {showLocationSearch && (
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <input
                type="text"
                placeholder="Enter address or city..."
                value={searchLocation}
                onChange={(e) => setSearchLocation(e.target.value)}
                className="p-2 border rounded"
              />
              <input
                type="text"
                placeholder="Keyword (e.g., Steel, Solar)"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                className="p-2 border rounded"
              />
              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="10"
                  max="500"
                  step="10"
                  value={searchRadius}
                  onChange={(e) => setSearchRadius(Number(e.target.value))}
                  className="flex-1"
                />
                <span className="text-sm w-16">{searchRadius} mi</span>
              </div>
              <button
                onClick={searchByLocation}
                disabled={searching}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 disabled:bg-gray-400 flex items-center justify-center"
              >
                {searching ? (
                  <FaSpinner className="animate-spin" />
                ) : (
                  "Search Area"
                )}
              </button>
            </div>

            {/* Nearby facilities results */}
            {nearbyFacilities.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold mb-2">
                  {nearbyFacilities.length} facilities within {searchRadius}{" "}
                  miles
                </h3>
                <div className="max-h-60 overflow-y-auto">
                  {nearbyFacilities.map((facility) => (
                    <div
                      key={facility.id}
                      className="bg-white p-3 mb-2 rounded border hover:bg-gray-50"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">{facility.name}</h4>
                          <p className="text-sm text-gray-600">
                            {facility.city}, {facility.state} •
                            <span className="font-medium ml-1">
                              {facility.distance.toFixed(1)} miles away
                            </span>
                          </p>
                          <p className="text-xs text-gray-500 mt-1">
                            {facility.services.slice(0, 3).join(", ")}
                            {facility.services.length > 3 && "..."}
                          </p>
                        </div>
                        <button
                          onClick={() => viewFacilityDetail(facility)}
                          className="text-blue-600 hover:text-blue-800"
                        >
                          <FaEye />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="">All Types</option>
            {facilityTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>

          <select
            value={filterService}
            onChange={(e) => setFilterService(e.target.value)}
            className="p-2 border rounded-md"
          >
            <option value="">All Services</option>
            {allServices.map((service) => (
              <option key={service} value={service}>
                {service}
              </option>
            ))}
          </select>

          <select
            value={filterState}
            onChange={(e) => {
              setFilterState(e.target.value);
              setFilterCity("");
            }}
            className="p-2 border rounded-md"
          >
            <option value="">All States</option>
            {states.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>

          <select
            value={filterCity}
            onChange={(e) => setFilterCity(e.target.value)}
            className="p-2 border rounded-md"
            disabled={!filterState && cities.length === 0}
          >
            <option value="">All Cities</option>
            {cities.map((city) => (
              <option key={city} value={city}>
                {city}
              </option>
            ))}
          </select>

          <button
            onClick={clearFilters}
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded flex items-center justify-center"
          >
            <FaFilter className="mr-2" />
            Clear
          </button>
        </div>
      </div>

      {/* Map View (optional - requires Google Maps JavaScript API) */}
      {showMap && (
        <div className="bg-white p-4 rounded-lg shadow mb-6">
          <div className="h-96 bg-gray-200 rounded flex items-center justify-center">
            <p className="text-gray-600">
              Map integration requires Google Maps JavaScript API setup
            </p>
          </div>
        </div>
      )}

      {/* Facilities Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <FaSpinner className="animate-spin text-3xl text-blue-600 mx-auto mb-4" />
            <p>Loading facilities...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-8 text-center text-gray-600">
            <FaFilter className="text-4xl mx-auto mb-4 text-gray-400" />
            <p>No facilities match your criteria</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Facility
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Services
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedData.map((facility) => (
                  <tr key={facility.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium text-gray-900">
                        {facility.name}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {facility.facility_type}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm">
                        <div className="flex items-center">
                          <FaMapMarkerAlt className="text-gray-400 mr-1" />
                          <span className="font-medium">
                            {facility.city}, {facility.state}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                          {facility.address}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs">
                        {facility.services.slice(0, 2).map((service, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-gray-100 rounded px-2 py-1 mr-1 mb-1"
                          >
                            {service}
                          </span>
                        ))}
                        {facility.services.length > 2 && (
                          <span className="text-gray-500">
                            +{facility.services.length - 2} more
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {facility.phone ? (
                        <div className="flex items-center text-gray-600">
                          <FaPhone className="mr-1 text-xs" />
                          {facility.phone}
                        </div>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => viewFacilityDetail(facility)}
                          className="text-blue-600 hover:text-blue-800"
                          title="View Details"
                        >
                          <FaEye />
                        </button>
                        <button
                          onClick={() => openGoogleMaps(facility)}
                          className="text-green-600 hover:text-green-800"
                          title="View on Map"
                        >
                          <FaMapMarkerAlt />
                        </button>
                        <button
                          onClick={() => findSimilarFacilities(facility)}
                          className="text-purple-600 hover:text-purple-800"
                          title="Find Similar"
                        >
                          <FaGlobe />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-center">
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-100"
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="px-3 py-1 border rounded disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-100"
            >
              Previous
            </button>
            <span className="px-4 text-sm">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-100"
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages}
              className="px-3 py-1 border rounded disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-100"
            >
              Last
            </button>
          </div>
        </div>
      )}

      {/* Facility Detail Modal */}
      {showDetail && selectedFacility && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-start">
              <div>
                <h2 className="text-xl font-bold">{selectedFacility.name}</h2>
                <p className="text-sm text-gray-600 mt-1">
                  {selectedFacility.facility_type} Facility
                </p>
              </div>
              <button
                onClick={() => {
                  setShowDetail(false);
                  setSelectedFacility(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Location Info */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
                    <FaMapMarkerAlt className="mr-2" />
                    Location Information
                  </h3>
                  <div className="bg-gray-50 rounded p-4 space-y-2">
                    <p className="text-sm">
                      <span className="font-medium">Address:</span>
                      <br />
                      {selectedFacility.address}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">City:</span>{" "}
                      {selectedFacility.city}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">State:</span>{" "}
                      {selectedFacility.state}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">ZIP Code:</span>{" "}
                      {selectedFacility.zip}
                    </p>
                    <p className="text-sm">
                      <span className="font-medium">Coordinates:</span>
                      <br />
                      {selectedFacility.coordinates.latitude.toFixed(6)},
                      {selectedFacility.coordinates.longitude.toFixed(6)}
                    </p>
                  </div>

                  {/* Map Actions */}
                  <div className="mt-4 space-y-2">
                    <button
                      onClick={() => openGoogleMaps(selectedFacility)}
                      className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                      <FaMapMarkerAlt />
                      <span>View on Google Maps</span>
                      <FaExternalLinkAlt className="text-xs" />
                    </button>
                  </div>
                </div>

                {/* Services and Contact */}
                <div>
                  <h3 className="font-semibold text-gray-700 mb-3 flex items-center">
                    <FaBuilding className="mr-2" />
                    Facility Details
                  </h3>
                  <div className="bg-gray-50 rounded p-4 space-y-4">
                    {selectedFacility.phone && (
                      <div>
                        <p className="text-sm font-medium mb-1">Contact:</p>
                        <div className="flex items-center text-sm text-gray-600">
                          <FaPhone className="mr-2" />
                          {selectedFacility.phone}
                        </div>
                      </div>
                    )}

                    <div>
                      <p className="text-sm font-medium mb-2">Services:</p>
                      <div className="flex flex-wrap gap-2">
                        {selectedFacility.services.map((service, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full"
                          >
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Search Similar */}
                  <div className="mt-4">
                    <h4 className="text-sm font-medium mb-2">
                      Find Similar Facilities:
                    </h4>
                    <div className="space-y-2">
                      <input
                        type="text"
                        placeholder="Enter keyword..."
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                        className="w-full p-2 border rounded text-sm"
                      />
                      <div className="flex items-center space-x-2">
                        <input
                          type="range"
                          min="10"
                          max="500"
                          step="10"
                          value={searchRadius}
                          onChange={(e) =>
                            setSearchRadius(Number(e.target.value))
                          }
                          className="flex-1"
                        />
                        <span className="text-sm w-16">{searchRadius} mi</span>
                      </div>
                      <button
                        onClick={() => findSimilarFacilities(selectedFacility)}
                        disabled={renewableLoading}
                        className="w-full bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:bg-gray-400 flex items-center justify-center"
                      >
                        {renewableLoading ? (
                          <FaSpinner className="animate-spin" />
                        ) : (
                          <>
                            <FaGlobe className="mr-2" />
                            Find Similar Facilities
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Renewable Businesses Modal */}
      {showRenewableModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gray-50 px-6 py-4 border-b flex justify-between items-center">
              <div>
                <h2 className="text-xl font-bold">
                  Renewable Energy Businesses
                </h2>
                <p className="text-sm text-gray-600">
                  Found {renewableBusinesses.length} businesses
                </p>
              </div>
              <button
                onClick={() => {
                  setShowRenewableModal(false);
                  setRenewableBusinesses([]);
                  setRenewablePage(1);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <FaTimes />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {renewableLoading ? (
                <div className="text-center py-8">
                  <FaSpinner className="animate-spin text-3xl text-blue-600 mx-auto mb-4" />
                  <p>Searching renewable businesses...</p>
                </div>
              ) : renewableBusinesses.length === 0 ? (
                <div className="text-center py-8 text-gray-600">
                  <p>No renewable businesses found</p>
                </div>
              ) : (
                <>
                  {/* Businesses Table */}
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Business
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Location
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Distance
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                          </th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {renewableBusinesses
                          .slice(
                            (renewablePage - 1) * renewableItemsPerPage,
                            renewablePage * renewableItemsPerPage
                          )
                          .map((business, index) => (
                            <tr
                              key={business.place_id || index}
                              className="hover:bg-gray-50"
                            >
                              <td className="px-4 py-3">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {business.name}
                                  </div>
                                  {business.types &&
                                    business.types.length > 0 && (
                                      <div className="flex flex-wrap gap-1 mt-1">
                                        {business.types
                                          .slice(0, 2)
                                          .map((type, idx) => (
                                            <span
                                              key={idx}
                                              className="inline-block bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full"
                                            >
                                              {type.replace(/_/g, " ")}
                                            </span>
                                          ))}
                                        {business.types.length > 2 && (
                                          <span className="text-xs text-gray-500">
                                            +{business.types.length - 2} more
                                          </span>
                                        )}
                                      </div>
                                    )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm">
                                  <div className="flex items-center">
                                    <FaMapMarkerAlt className="text-gray-400 mr-1" />
                                    <span>
                                      {business.formatted_address ||
                                        `${business.location?.lat?.toFixed(
                                          4
                                        )}, ${business.location?.lng?.toFixed(
                                          4
                                        )}`}
                                    </span>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm text-gray-600">
                                  {business.distance_m
                                    ? `${(
                                        business.distance_m / 1609.34
                                      ).toFixed(1)} mi`
                                    : "—"}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="text-sm space-y-1">
                                  {business.phone && (
                                    <div className="flex items-center text-gray-600">
                                      <FaPhone className="mr-1 text-xs" />
                                      <a
                                        href={`tel:${business.phone}`}
                                        className="hover:text-blue-600"
                                      >
                                        {business.phone}
                                      </a>
                                    </div>
                                  )}
                                  {business.website && (
                                    <div className="flex items-center text-gray-600">
                                      <FaGlobe className="mr-1 text-xs" />
                                      <a
                                        href={business.website}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="hover:text-blue-600 truncate max-w-32"
                                      >
                                        {business.website.replace(
                                          /^https?:\/\//,
                                          ""
                                        )}
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center space-x-2">
                                  {business.location && (
                                    <button
                                      onClick={() => {
                                        const url = `https://www.google.com/maps/search/?api=1&query=${business.location.lat},${business.location.lng}`;
                                        window.open(url, "_blank");
                                      }}
                                      className="text-green-600 hover:text-green-800"
                                      title="View on Map"
                                    >
                                      <FaMapMarkerAlt />
                                    </button>
                                  )}
                                  {business.website && (
                                    <button
                                      onClick={() =>
                                        window.open(business.website, "_blank")
                                      }
                                      className="text-blue-600 hover:text-blue-800"
                                      title="Visit Website"
                                    >
                                      <FaExternalLinkAlt />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {renewableBusinesses.length > renewableItemsPerPage && (
                    <div className="mt-6 flex justify-center">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setRenewablePage(1)}
                          disabled={renewablePage === 1}
                          className="px-3 py-1 border rounded disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-100"
                        >
                          First
                        </button>
                        <button
                          onClick={() => setRenewablePage(renewablePage - 1)}
                          disabled={renewablePage === 1}
                          className="px-3 py-1 border rounded disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-100"
                        >
                          Previous
                        </button>
                        <span className="px-4 text-sm">
                          Page {renewablePage} of{" "}
                          {Math.ceil(
                            renewableBusinesses.length / renewableItemsPerPage
                          )}
                        </span>
                        <button
                          onClick={() => setRenewablePage(renewablePage + 1)}
                          disabled={
                            renewablePage ===
                            Math.ceil(
                              renewableBusinesses.length / renewableItemsPerPage
                            )
                          }
                          className="px-3 py-1 border rounded disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-100"
                        >
                          Next
                        </button>
                        <button
                          onClick={() =>
                            setRenewablePage(
                              Math.ceil(
                                renewableBusinesses.length /
                                  renewableItemsPerPage
                              )
                            )
                          }
                          disabled={
                            renewablePage ===
                            Math.ceil(
                              renewableBusinesses.length / renewableItemsPerPage
                            )
                          }
                          className="px-3 py-1 border rounded disabled:bg-gray-100 disabled:text-gray-400 hover:bg-gray-100"
                        >
                          Last
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Collectors;
